import React from "react";
import useProfileEdit from "../../hooks/useProfileEdit";
import ChangePasswordForm from "../../components/ChangePasswordForm";
import SafeImage from "../../components/common/SafeImage";

export default function AdminProfileSettings({ role = "admin" }) {
  const {
    profile,
    handleChange,
    profileImage,
    handleImageChange,
    handleImageSubmit,
    profileImageUrl,
    handleProfileSubmit,
    loading,
    fields,
  } = useProfileEdit(role);

  // Helper to get correct profile image URL
  const getProfileImageUrl = () => {
    if (!profileImageUrl) return "/assets/images/users/1.jpg";
    if (profileImageUrl.startsWith("/")) {
      return (
        (import.meta.env.VITE_API_URL || "http://localhost:8000") +
        profileImageUrl
      );
    }
    return profileImageUrl;
  };

  return (
    <div>
      <div className="title-header option-title">
        <h5>Profile Settings</h5>
      </div>
      <form
        className="theme-form theme-form-2 mega-form"
        onSubmit={handleProfileSubmit}
      >
        <div className="card-header-1 mb-3">
          <h2>Personal Information</h2>
        </div>
        {fields.includes("name") && (
          <div className="mb-4 row align-items-center">
            <label className="form-label-title col-lg-2 col-md-3 mb-0">
              Full Name
            </label>
            <div className="col-md-9 col-lg-10">
              <input
                className="form-control"
                type="text"
                name="name"
                value={profile.name}
                onChange={handleChange}
                placeholder="Enter your full name"
              />
            </div>
          </div>
        )}
        {fields.includes("email") && (
          <div className="mb-4 row align-items-center">
            <label className="form-label-title col-lg-2 col-md-3 mb-0">
              Email Address
            </label>
            <div className="col-md-9 col-lg-10">
              <input
                className="form-control"
                type="email"
                name="email"
                value={profile.email}
                onChange={handleChange}
                placeholder="Enter your email"
              />
            </div>
          </div>
        )}
        {fields.includes("phone_no") && (
          <div className="mb-4 row align-items-center">
            <label className="form-label-title col-lg-2 col-md-3 mb-0">
              Phone Number
            </label>
            <div className="col-md-9 col-lg-10">
              <input
                className="form-control"
                type="tel"
                name="phone_no"
                value={profile.phone_no}
                onChange={handleChange}
                placeholder="Enter your phone number"
              />
            </div>
          </div>
        )}
        {fields.includes("longitude") && (
          <div className="mb-4 row align-items-center">
            <label className="form-label-title col-lg-2 col-md-3 mb-0">
              Longitude
            </label>
            <div className="col-md-9 col-lg-10">
              <input
                className="form-control"
                type="number"
                name="longitude"
                value={profile.longitude}
                onChange={handleChange}
                placeholder="Longitude"
              />
            </div>
          </div>
        )}
        {fields.includes("latitude") && (
          <div className="mb-4 row align-items-center">
            <label className="form-label-title col-lg-2 col-md-3 mb-0">
              Latitude
            </label>
            <div className="col-md-9 col-lg-10">
              <input
                className="form-control"
                type="number"
                name="latitude"
                value={profile.latitude}
                onChange={handleChange}
                placeholder="Latitude"
              />
            </div>
          </div>
        )}
        <div className="card-submit-button">
          <button
            className="btn btn-animation ms-auto"
            type="submit"
            disabled={loading}
          >
            {loading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
      {/* Profile Picture (if supported) */}
      {handleImageSubmit && (
        <form
          className="theme-form theme-form-2 mega-form mt-4"
          onSubmit={handleImageSubmit}
        >
          <div className="card-header-1 mt-4 mb-3">
            <h2>Profile Picture</h2>
          </div>
          <div className="mb-4 row align-items-center">
            <div className="col-md-9 col-lg-10">
              <input
                className="form-control"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              <div className="mt-3">
                {profileImage && (
                  <div className="mb-3">
                    <small className="text-success d-block mb-2">
                      ✓ Selected: {profileImage.name}
                    </small>
                    <div className="d-flex align-items-center gap-3">
                      <div>
                        <small className="text-muted d-block">Preview:</small>
                        <SafeImage
                          src={URL.createObjectURL(profileImage)}
                          alt="Preview"
                          style={{
                            width: 80,
                            height: 80,
                            objectFit: "cover",
                            borderRadius: 8,
                            border: "2px solid #28a745",
                          }}
                          fallbackSrc="/assets/images/users/1.jpg"
                        />
                      </div>
                      {profileImageUrl && (
                        <div>
                          <small className="text-muted d-block">Current:</small>
                          <SafeImage
                            src={getProfileImageUrl()}
                            alt="Current Profile"
                            style={{
                              width: 80,
                              height: 80,
                              objectFit: "cover",
                              borderRadius: 8,
                              border: "1px solid #ddd",
                            }}
                            fallbackSrc="/assets/images/users/1.jpg"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {!profileImage && profileImageUrl && (
                  <div className="mb-2">
                    <small className="text-muted d-block mb-2">
                      Current Profile Picture:
                    </small>
                    <SafeImage
                      src={getProfileImageUrl()}
                      alt="Current Profile"
                      style={{
                        width: 100,
                        height: 100,
                        objectFit: "cover",
                        borderRadius: 8,
                        border: "1px solid #ddd",
                      }}
                      fallbackSrc="/assets/images/users/1.jpg"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="card-submit-button mb-4">
            <button
              className="btn btn-animation ms-auto"
              type="submit"
              disabled={loading || !profileImage}
            >
              {loading ? "Uploading..." : "Upload Image"}
            </button>
          </div>
        </form>
      )}
      {/* Change Password Section */}
      <ChangePasswordForm endpoint="/admin/change-password" />
    </div>
  );
}
